import React, { useEffect, useState } from 'react';
import './style.scss';
import { useHistory } from 'react-router-dom';
import {
  IBillingAccountInfo,
  IMidasAccountInfo,
  MidasAccountStatus,
} from '../../../../model/billing-center';
import { GetformatMoney } from '../billing-overview/component/fomat-money';
import {
  getRolesList,
  getUserGroupsList,
  getStudioMembersHasPermission,
  getMidasAccountInfo,
} from '../../../../api';
import { RbacRoleType } from '../../../../model';
import { RoleType } from '../../../../model/api-modules';
import { AutoTable } from '../../../../components/auto-table';
import { getLocalTime } from '../../../../lib';
import getFormatMessage from '../../../../locales/get-format-message';
import { useSelector } from 'react-redux';
import { IRootState } from '../../../../store/reducers';

interface IProps {
  readonly studioId: string;
  readonly balanceList: IBillingAccountInfo | null;
  readonly studioName: string;
}

interface IBillingAccountList {
  readonly userID: string;
  readonly userName: string;
  readonly roleType: string;
  readonly groupNameList: string[];
}

export const AccountManagement = (props: IProps) => {
  const [midasAccountList, setMidasAccountList] =
    useState<IMidasAccountInfo | null>(null);
  const [accountList, setAccountList] = useState<IBillingAccountList[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const history = useHistory();
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);
  const { studioId } = props;
  useEffect(() => {
    queryMembersList();
    queryMidasAccountInfo();
  }, []);

  const queryMidasAccountInfo = async () => {
    const res = await getMidasAccountInfo(props.studioId);
    if (res) {
      setMidasAccountList(res);
    }
  };

  const queryMembersList = async () => {
    setLoading(true);
    const membersList: IBillingAccountList[] = [];
    const [roleRes, groupRes, membersRes] = await Promise.all([
      getRolesList(studioId),
      getUserGroupsList(studioId),
      getStudioMembersHasPermission(studioId),
    ]);
    if (roleRes && roleRes.length !== 0) {
      const expanseRole: number[] = [];
      const expanseGroup: number[] = [];
      if (groupRes && groupRes.length !== 0) {
        roleRes.forEach((roleItem) => {
          if (
            roleItem.permissions.includes('feeCenterView') &&
            roleItem.permissions.includes('feeCenterEdit') &&
            roleItem.type === RbacRoleType.Custom
          ) {
            expanseRole.push(roleItem.roleId);
          }
        });
        groupRes.forEach((groupItem) => {
          const itemList = groupItem.roles.filter((item) =>
            expanseRole.includes(item.roleId)
          );
          if (itemList.length !== 0) {
            expanseGroup.push(groupItem.groupId);
          }
        });
      }
      if (membersRes && membersRes.length !== 0) {
        membersRes.forEach((memberItem) => {
          const itemGroupName: string[] = [];
          let roleType = '';
          if (memberItem.RbacMember && memberItem.RbacMember.length !== 0) {
            memberItem.RbacMember.forEach((rbacMemberItem) => {
              if (
                rbacMemberItem.roleType === RoleType.Owner ||
                rbacMemberItem.roleType === RoleType.Admin
              ) {
                roleType = rbacMemberItem.roleType;
              }
              if (
                rbacMemberItem.groupId &&
                expanseGroup.includes(rbacMemberItem.groupId) &&
                rbacMemberItem.groupName &&
                !itemGroupName.includes(rbacMemberItem.groupName)
              ) {
                itemGroupName.push(rbacMemberItem.groupName);
              }
            });
          }
          if (roleType || itemGroupName.length !== 0) {
            membersList.push({
              userID: memberItem.UserID,
              userName: memberItem.UserName,
              roleType,
              groupNameList: itemGroupName,
            });
          }
        });
      }
      setAccountList(membersList);
    }
    setLoading(false);
  };

  const handleRechargeClick = () => {
    history.push(`/studio/${studioId}/fee/overview/recharge`);
  };
  const handleWithDrawalClick = () => {
    history.push(`/studio/${studioId}/fee/overview/withdrawal`);
  };
  const handleBalanceClick = () => {
    history.push(`/studio/${studioId}/fee/overview/transaction-details`);
  };
  const goToRolePage = () => {
    history.push(`/studio/${studioId}/organization?tabKey=Roles`);
  };

  const capitalizeFirstLetter = (str: string) => {
    return str.toLowerCase().replace(/\b[a-z]/g, (match) => {
      return match.toUpperCase();
    });
  };

  const onChangedFilterValues = () => {};
  const pageList = [...accountList].slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const COLUMNS = [
    {
      name: getFormatMessage('account_management_name'),
      key: 'userID',
      tableExtension: {
        renderRow(_key: any, _value: string, row: any) {
          const record = row as IBillingAccountList;
          return (
            <>
              <span>{`${record.userName}(${_value})`}</span>
              {(record.roleType === RoleType.Owner ||
                record.roleType === RoleType.Admin) && (
                <span
                  style={{
                    display: 'inline-block',
                    width: '46px',
                    height: '18px',
                    lineHeight: '18px',
                    background: '#f2f3f5',
                    color: '#86909c',
                    borderRadius: '2px',
                    marginLeft: '4px',
                    textAlign: 'center',
                    fontSize: '12px',
                  }}
                >
                  {capitalizeFirstLetter(record.roleType)}
                </span>
              )}
            </>
          );
        },
      },
    },
    // {
    //   name: '授权时间',
    //   key: '',
    // },
    {
      name: getFormatMessage('account_management_group'),
      key: 'groupNameList',
      tableExtension: {
        renderRow(_key: any, _value: string[]) {
          let groupName = '';
          _value.forEach((item, index) => {
            if (index === 0) {
              groupName += item;
            } else {
              groupName += `, ${item}`;
            }
          });
          return <>{groupName}</>;
        },
      },
    },
  ];

  return (
    <div className="account-management">
      <div className="account-management-top">
        <div className="account-management-left">
          <div className="account-management-item-top">
            {getFormatMessage('account_management_info')}
          </div>
          <div className="account-management-left-bottom">
            <div className="account-management-left-bottom-item">
              <div className="account-management-left-bottom-item-top">
                {getFormatMessage('account_management_id')}
              </div>
              <div className="account-management-left-bottom-item-bottom">
                {midasAccountList?.account || ''}
              </div>
            </div>
            <div className="account-management-left-bottom-item">
              <div className="account-management-left-bottom-item-top">
                {getFormatMessage('account_management_owned_by')}
              </div>
              <div className="account-management-left-bottom-item-bottom">
                {props.studioName}
              </div>
            </div>
            <div className="account-management-left-bottom-item">
              <div className="account-management-left-bottom-item-top">
                {getFormatMessage('account_management_status')}
              </div>
              <div className="account-management-left-bottom-item-bottom">
                {midasAccountList &&
                  midasAccountList?.account_status ===
                    MidasAccountStatus.NORMAL && (
                    <>
                      <span className="account-management-icon account-management-green"></span>
                      <span>
                        {getFormatMessage('account_management_status_active')}
                      </span>
                    </>
                  )}
                {midasAccountList &&
                  midasAccountList?.account_status ===
                    MidasAccountStatus.OVERDUE && (
                    <>
                      <span className="account-management-icon"></span>
                      <span>
                        {getFormatMessage('account_management_status_abnormal')}
                      </span>
                    </>
                  )}
                {midasAccountList &&
                  midasAccountList?.account_status ===
                    MidasAccountStatus.UNANTHORIZED && (
                    <>
                      <span className="account-management-icon"></span>
                      <span>
                        {getFormatMessage(
                          'account_management_status_Unauthenticated'
                        )}
                      </span>
                    </>
                  )}
              </div>
            </div>
            <div className="account-management-left-bottom-item">
              <div className="account-management-left-bottom-item-top">
                {getFormatMessage('account_management_creation_time')}
              </div>
              <div className="account-management-left-bottom-item-bottom">
                {getLocalTime(
                  midasAccountList?.create_time || '',
                  userInfo?.UserSettings?.Timezone
                )}
              </div>
            </div>
          </div>
        </div>
        <div>
          <div className="account-management-right">
            <div className="account-management-item-top">
              <div>{getFormatMessage('account_management_assets')}</div>
              <div className="account-management-right-btn-box">
                {/* <div className="account-management-right-btn-img-box">
                  <img src={messageIcon} className="account-management-right-btn-img" />
                </div> */}
                <div
                  className="gc-btn gc-btn-default account-management-right-btn-item"
                  onClick={handleRechargeClick}
                >
                  {getFormatMessage('account_management_pay')}
                </div>
                <div
                  className="gc-btn gc-btn-default account-management-right-btn-item"
                  onClick={handleWithDrawalClick}
                >
                  {getFormatMessage('account_management_withdrawal')}
                </div>
                <div
                  className="gc-btn gc-btn-default account-management-right-btn-item"
                  onClick={handleBalanceClick}
                >
                  {getFormatMessage('account_management_details')}
                </div>
              </div>
            </div>
            <div className="account-management-right-bottom">
              <div className="account-management-right-bottom-left">
                <GetformatMoney
                  className="normal-account-money"
                  money={props.balanceList?.account_balance}
                  isNegative={
                    props.balanceList?.account_balance !== undefined &&
                    Number(props.balanceList.account_balance) < 0
                  }
                />
              </div>
              {/* <div className="account-management-right-bottom-right">
                余额预警
              </div> */}
            </div>
          </div>
        </div>
      </div>
      <div className="account-management-bottom">
        <div className="account-management-item-top">
          {getFormatMessage('account_management_admin')}
        </div>
        <div className="account-management-bottom-tips">
          <span>{getFormatMessage('account_management_tips')}</span>
          <span
            className="account-management-bottom-tips-btn"
            onClick={goToRolePage}
          >
            {getFormatMessage('account_management_config')}
          </span>
        </div>
        <AutoTable
          loading={loading}
          list={pageList || []}
          configFields={COLUMNS}
          tableExtension={{
            onChange: (changeInfo) => {
              if (!changeInfo) {
                return;
              }
              setCurrentPage(changeInfo.current);
              setPageSize(changeInfo.pageSize || 10);
            },
            pagination: {
              current: currentPage,
              total: accountList.length,
              pageSize,
              showTotal: () => {
                return `Total ${accountList.length} items`;
              },
            },
          }}
          layoutExtension={{
            showTopbar: false,
            showFilter: true,
          }}
          onChangedFilterValues={onChangedFilterValues}
        />
      </div>
    </div>
  );
};
